<template>
  <div>
    <headline-container headline="Create User" :breadcrumbs="breadcrumbs"></headline-container>

    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Create User
            </h3>

            <p>
              Enter the details of the user. If an accounting system reference is added, the user will be
              shown on the invoices in the accounting system that he or she is responsible for.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <text-form-group
                    v-model="data.first_name"
                    name="first_name"
                    label="First Name"
                    help-text="The first name of the user."
                    :error="getError('first_name')"
                ></text-form-group>

                <text-form-group
                    v-model="data.last_name"
                    name="last_name"
                    label="Last Name"
                    help-text="The last name of the user."
                    :error="getError('last_name')"
                ></text-form-group>

                <text-form-group
                    v-model="data.initials"
                    name="initials"
                    label="Initials"
                    help-text="The initials of the user."
                    :error="getError('initials')"
                ></text-form-group>

                <text-form-group
                    v-model="data.title"
                    name="title"
                    label="Title"
                    help-text="The title of the user."
                    :error="getError('title')"
                ></text-form-group>

                <email-form-group
                    v-model="data.email"
                    name="email"
                    label="Email"
                    help-text="The user's email."
                    :error="getError('email')"
                ></email-form-group>

                <text-form-group
                    v-model="data.phone"
                    name="phone"
                    label="Phone Number"
                    help-text="The phone number of the user."
                    :error="getError('phone')"
                ></text-form-group>

                <text-form-group
                    v-model="data.linkedin"
                    name="linkedin"
                    label="LinkedIn"
                    help-text="A link to the user's LinkedIn profile."
                    :error="getError('linkedin')"
                ></text-form-group>

                <color-form-group
                    v-model="data.color"
                    name="color"
                    label="Color"
                    help-text="The color of the user."
                    :colors="colors.data"
                    :error="getError('color')"
                ></color-form-group>

                <password-form-group
                    v-model="data.password"
                    name="password"
                    label="Password"
                    help-text="The password of the user. This can be changed by the user afterwards."
                    :error="getError('password')"
                ></password-form-group>

                <password-form-group
                    v-model="data.password_confirmation"
                    name="password_confirmation"
                    label="Password Confirmation"
                    help-text="Type the password again."
                    :error="getError('password_confirmation')"
                ></password-form-group>

                <select-form-group
                    v-model="data.accounting_system_number"
                    name="accounting_system_number"
                    label="Accounting System Reference"
                    help-text="The employee in the accounting system that corresponds to this user."
                    :error="getError('accounting_system_number')"
                    :options="accountingSystemNumberOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="data.email_tracking_account_reference"
                    name="email_tracking_account_reference"
                    label="Email Tracking Account"
                    help-text="The account in the email tracking system that corresponds to this user."
                    :error="getError('email_tracking_account_reference')"
                    :options="emailTrackingAccountOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="data.slack_reference"
                    name="slack_reference"
                    label="Slack User"
                    help-text="The Slack user that corresponds to this user."
                    :error="getError('slack_reference')"
                    :options="slackUserOptions"
                    :nullable="true"
                ></select-form-group>

                <radio-form-group
                    v-model="data.is_default_subscription_responsible"
                    name="is_default_subscription_responsible"
                    label="Default Subscription Responsible"
                    help-text="A flag indicating whether the user is the default responsible for new subscriptions."
                    :error="getError('is_default_subscription_responsible')"
                    :options="[{value: false, label: 'No'}, {value: true, label: 'Yes'}]"
                ></radio-form-group>

                <gallery-form-group
                    v-model="data.image_id"
                    name="image_id"
                    label="Image"
                    :error="getError('image_id')"
                    :nullable="true"
                ></gallery-form-group>

                <hr>

                <submit-form-group button-class="btn-black"></submit-form-group>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import ColorFormGroup from '@/components/FormGroups/Horizontal/Color'
import Errors from '@/mixins/Errors'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import GalleryFormGroup from '@/components/FormGroups/Horizontal/Gallery'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import PasswordFormGroup from '@/vendor/forms/FormGroups/Horizontal/Password'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'

export default {
  components: {
    ColorFormGroup,
    EmailFormGroup,
    GalleryFormGroup,
    HeadlineContainer,
    HelpText,
    LoadingContainer,
    PasswordFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TextFormGroup
  },

  mixins: [Errors, Load],

  data() {
    return {
      data: {},

      accountingSystemEmployees: null,
      emailTrackingAccounts: null,
      slackUsers: null,
      colors: null
    }
  },

  mounted() {
    this.load({
      accountingSystemEmployees: '/api/v2/bookkeeping-employees',
      emailTrackingAccounts: '/api/v2/email-tracking-accounts',
      slackUsers: '/api/v2/slack-users',
      colors: '/api/v2/colors'
    })
  },

  computed: {
    breadcrumbs() {
      return [
        {url: '/users', text: 'Users'},
        {text: 'Create'}
      ]
    },

    accountingSystemNumberOptions() {
      return this.accountingSystemEmployees.data.map(accountingSystemEmployee => {
        return {
          value: accountingSystemEmployee.id,
          label: accountingSystemEmployee.attributes.name
        }
      })
    },

    emailTrackingAccountOptions() {
      return this.emailTrackingAccounts.data.map(emailTrackingAccount => {
        return {
          value: emailTrackingAccount.id,
          label: emailTrackingAccount.attributes.name
        }
      })
    },

    slackUserOptions() {
      return this.slackUsers.data.map(slackUser => {
        return {
          value: slackUser.id,
          label: slackUser.attributes.name
        }
      })
    }
  },

  methods: {
    submit() {
      http.post('/api/v2/users', this.data)
          .then(() => {
            notify.success('The user was saved')

            this.$router.push('/users')
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
